import { Head } from 'vite-react-ssg';
import { TrackedLink } from "@/components/TrackedLink";
import { Link } from "react-router-dom";
import { Brain, Cpu, Zap, DollarSign, CheckCircle, XCircle, Chrome } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Breadcrumbs from "@/components/Breadcrumbs";

const DeepseekVsLlama = () => {
  const faqItems = [
    {
      question: "What is the main difference between DeepSeek and Llama?",
      answer: "DeepSeek R1 is optimized for advanced reasoning and chain-of-thought capabilities, while Llama 4 (Meta's latest model) focuses on multimodal capabilities and efficient performance across diverse tasks. Both are open-source, but DeepSeek excels in reasoning while Llama is more versatile for general applications."
    },
    {
      question: "Which is better for coding: DeepSeek or Llama 4?",
      answer: "DeepSeek is generally better for coding tasks, especially complex problem-solving and debugging. DeepSeek Coder and R1 are specifically optimized for code generation. Llama 4 is capable at coding but doesn't specialize in it."
    },
    {
      question: "Is DeepSeek or Llama 4 more cost-effective?",
      answer: "Both are highly cost-effective since they're open-source and can run locally. DeepSeek's API pricing is slightly lower, but both offer excellent value compared to proprietary models like GPT-4 or Claude."
    },
    {
      question: "Can I run DeepSeek and Llama locally?",
      answer: "Yes, both models are open-source and can run locally using tools like Ollama, vLLM, or llama.cpp. Smaller versions of both models can run on consumer hardware with 16GB+ VRAM."
    },
    {
      question: "Which model is better for reasoning tasks?",
      answer: "DeepSeek R1 is superior for reasoning tasks. It's specifically designed for chain-of-thought reasoning and achieves state-of-the-art results on math and logic benchmarks. Llama 4 is good at reasoning but not as specialized."
    },
    {
      question: "Does Llama 4 support multimodal inputs?",
      answer: "Yes, Llama 4 supports multimodal inputs including images and video. DeepSeek also has multimodal capabilities with DeepSeek-VL, but Llama 4's native multimodal support is more integrated."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const comparisonSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "DeepSeek vs Llama 4: Complete Open-Source AI Comparison 2025",
    "description": "Comprehensive comparison of DeepSeek R1 and Meta's Llama 4. Compare features, performance, pricing, and use cases.",
    "author": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide"
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-12-21"
  };

  return (
    <>
      <Head>
        <title>DeepSeek vs Llama 4 (2026) — Open-Source AI Comparison</title>
        <meta 
          name="description" 
          content="DeepSeek vs Llama 4 in 2026: reasoning, coding, multimodal, pricing and self-hosting. Honest side-by-side comparison to pick the best open-source AI." 
        />
        <meta name="keywords" content="DeepSeek vs Llama, Llama 4 vs DeepSeek, open-source AI comparison, DeepSeek Llama, AI comparison 2026, Meta Llama" />
        <link rel="canonical" href="https://deepseek.ai/deepseek-vs-llama" />
        
        <meta property="og:title" content="DeepSeek vs Llama 4 (2026) — Open-Source AI Showdown" />
        <meta property="og:description" content="DeepSeek vs Llama 4 in 2026: full open-source AI comparison covering reasoning vs multimodal, benchmark scores, pricing, self-hosting cost and the best model for each use case." />
        <meta property="og:url" content="https://deepseek.ai/deepseek-vs-llama" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek vs Llama 4 (2026) — Open-Source AI Showdown" />
        <meta name="twitter:description" content="DeepSeek vs Llama 4 in 2026: reasoning vs multimodal, benchmarks, pricing, self-hosting and the best fit per use case." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(comparisonSchema)}
        </script>
      </Head>

      <main className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Breadcrumbs 
          items={[{ label: "Compare", path: "/deepseek-vs-chatgpt" }]} 
          currentPage="DeepSeek vs Llama" 
        />

        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">DeepSeek vs Llama 4</h1>
          <p className="text-xl text-muted-foreground">
            Two open-source AI giants compared. DeepSeek's reasoning prowess vs Meta's multimodal versatility.
          </p>
        </div>

        {/* Quick Comparison */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <Brain className="h-10 w-10 text-primary mr-3" />
                <h2 className="text-2xl font-bold">DeepSeek R1</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Chinese open-source model optimized for deep reasoning, mathematics, and code generation with chain-of-thought capabilities.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Superior reasoning & chain-of-thought
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Best-in-class code generation
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  MIT license - fully open
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Extremely cost-effective API
                </li>
              </ul>
            </div>

            <div className="p-8 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <Cpu className="h-10 w-10 text-blue-500 mr-3" />
                <h2 className="text-2xl font-bold">Llama 4</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Meta's latest open-source model with native multimodal support, efficient architecture, and broad capabilities.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Native multimodal (image/video)
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Versatile for many tasks
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Strong community & ecosystem
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Wide hardware compatibility
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Detailed Comparison Table */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Feature Comparison</h2>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Feature</TableHead>
                  <TableHead className="text-center">DeepSeek R1</TableHead>
                  <TableHead className="text-center">Llama 4</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Developer</TableCell>
                  <TableCell className="text-center">DeepSeek (China)</TableCell>
                  <TableCell className="text-center">Meta (USA)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Primary Strength</TableCell>
                  <TableCell className="text-center">Reasoning & Coding</TableCell>
                  <TableCell className="text-center">Multimodal & Versatility</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Open Source</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">License</TableCell>
                  <TableCell className="text-center">MIT</TableCell>
                  <TableCell className="text-center">Llama License</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Multimodal Support</TableCell>
                  <TableCell className="text-center">Via DeepSeek-VL</TableCell>
                  <TableCell className="text-center">Native</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Math & Reasoning</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Good</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Code Generation</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Good</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Local Deployment</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">API Pricing</TableCell>
                  <TableCell className="text-center">Very Low</TableCell>
                  <TableCell className="text-center">Low (varies by provider)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Context Window</TableCell>
                  <TableCell className="text-center">128K tokens</TableCell>
                  <TableCell className="text-center">128K tokens</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </section>

        {/* Use Cases */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">When to Use Each</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Brain className="h-6 w-6 text-primary mr-2" />
                Choose DeepSeek When...
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Complex coding and debugging tasks
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Advanced mathematical reasoning
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Step-by-step problem solving
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Need visible reasoning process
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Maximum cost efficiency
                </li>
              </ul>
            </div>

            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Cpu className="h-6 w-6 text-blue-500 mr-2" />
                Choose Llama 4 When...
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  Processing images or videos
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  General-purpose applications
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  Need broad ecosystem support
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  Content creation & creative tasks
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  Prefer Meta's licensing terms
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Pricing Comparison</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <DollarSign className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="text-xl font-semibold">DeepSeek Pricing</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Free tier available</li>
                <li>• API: ~$0.14/million input tokens</li>
                <li>• ~$0.28/million output tokens</li>
                <li>• Free local deployment</li>
              </ul>
            </div>

            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <DollarSign className="h-6 w-6 text-blue-500 mr-2" />
                <h3 className="text-xl font-semibold">Llama 4 Pricing</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Free to download & use</li>
                <li>• API via providers (varies)</li>
                <li>• ~$0.20-0.50/million tokens</li>
                <li>• Free local deployment</li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                <AccordionContent>{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Related Comparisons */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">More AI Comparisons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/deepseek-vs-chatgpt" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs ChatGPT</h3>
              <p className="text-muted-foreground">Compare with OpenAI's flagship model.</p>
            </Link>
            <Link to="/deepseek-vs-claude" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs Claude</h3>
              <p className="text-muted-foreground">Compare with Anthropic's Claude.</p>
            </Link>
            <Link to="/deepseek-vs-mistral" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs Mistral</h3>
              <p className="text-muted-foreground">Compare with Mistral AI's models.</p>
            </Link>
            <Link to="/deepseek-vs-stepfun" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs Stepfun</h3>
              <p className="text-muted-foreground">Compare with Stepfun's Step-3.5 Flash multimodal lab.</p>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <h2 className="text-2xl font-bold mb-4">Try DeepSeek AI Today</h2>
          <p className="text-muted-foreground mb-6">Experience the power of advanced AI reasoning for free.</p>
          <a
            href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-primary hover:bg-primary/90 transition-all shadow-lg"
          >
            <Chrome className="mr-2 h-5 w-5" />
            Add to Chrome - It's Free
          </a>
        </section>
        {/* Explore further - tracked CTAs */}
        <section className="mt-12 mb-12 p-6 rounded-xl bg-muted/40 border text-center">
          <h3 className="text-lg font-semibold mb-2">Want to go deeper?</h3>
          <p className="text-sm text-muted-foreground mb-4">See what DeepSeek actually costs and how to integrate it.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <TrackedLink
              to="/pricing"
              source="deepseek-vs-llama"
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              See DeepSeek pricing →
            </TrackedLink>
            <TrackedLink
              to="/docs"
              source="deepseek-vs-llama"
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full border-2 border-primary text-primary bg-background hover:bg-accent transition-colors"
            >
              Read the API docs →
            </TrackedLink>
          </div>
        </section>
      </main>
    </>
  );
};

export default DeepseekVsLlama;
